<template>
  <b-overlay :show="loading">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h4 class="card-title mb-0">Mis Tipos de Documentos</h4>
        <div class="d-flex gap-2 align-items-center">
          <span class="text-muted small" v-if="totalCount > 0">
            Mostrando {{ documentTypes.length }} de {{ totalCount }} tipos de documentos
          </span>
          <b-button variant="primary" size="sm" @click="refreshList" :disabled="loading">
            <feather-icon icon="RefreshCwIcon" class="mr-1" v-if="!loading" />
            <span class="spinner-border spinner-border-sm mr-1" role="status" v-else />
            Actualizar
          </b-button>
        </div>
      </div>
      <div class="card-body">
        <!-- Mensaje cuando no hay tipos de documentos -->
        <div v-if="!loading && documentTypes.length === 0" class="text-center py-5">
          <feather-icon icon="FileTextIcon" size="64" class="text-muted mb-3" />
          <h5 class="text-muted">No hay tipos de documentos disponibles</h5>
          <p class="text-muted">No se encontraron tipos de documentos en el sistema.</p>
        </div>

        <!-- Tabla de tipos de documentos -->
        <b-table
          v-else
          :items="documentTypes"
          :fields="fields"
          :busy="loading"
          :empty-text="emptyText"
          responsive="sm"
          striped
          hover
          show-empty
        >
          <template v-slot:cell(tipoId)="row">
            <strong>{{ row.item.tipoId }}</strong>
          </template>
          <template v-slot:cell(nombre)="row">
            {{ row.item.nombre }}
          </template>
          <template v-slot:cell(descripcion)="row">
            <span v-if="row.item.descripcion">{{ row.item.descripcion }}</span>
            <span class="text-muted" v-else>—</span>
          </template>
          <template v-slot:cell(activo)="row">
            <b-badge :variant="row.item.activo ? 'success' : 'secondary'">
              {{ row.item.activo ? 'Activo' : 'Inactivo' }}
            </b-badge>
          </template>
          <template v-slot:cell(actions)="row">
            <div class="d-flex gap-1">
              <b-button
                size="sm"
                variant="outline-primary"
                @click="viewDocumentTypeDetails(row.item)"
                title="Ver detalles"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Paginación -->
        <b-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          :aria-controls="false"
          align="center"
          size="sm"
          class="mt-3"
          @input="onPageChange"
        />
      </div>

      <!-- Modal de detalles de tipo de documento -->
      <b-modal
        v-model="showDetailModal"
        :title="modalTitle"
        size="lg"
        centered
      >
        <div v-if="selectedDocumentType">
          <dl class="row mb-0">
            <dt class="col-sm-3">ID Tipo</dt>
            <dd class="col-sm-9">{{ selectedDocumentType.tipoId }}</dd>

            <dt class="col-sm-3">Nombre</dt>
            <dd class="col-sm-9">{{ selectedDocumentType.nombre }}</dd>

            <dt class="col-sm-3">Descripción</dt>
            <dd class="col-sm-9">
              <span v-if="selectedDocumentType.descripcion">
                {{ selectedDocumentType.descripcion }}
              </span>
              <span class="text-muted" v-else>—</span>
            </dd>

            <dt class="col-sm-3">Estado</dt>
            <dd class="col-sm-9">
              <b-badge
                :variant="selectedDocumentType.activo ? 'success' : 'secondary'"
              >
                {{ selectedDocumentType.activo ? 'Activo' : 'Inactivo' }}
              </b-badge>
            </dd>

            <dt class="col-sm-3">Fecha Creación</dt>
            <dd class="col-sm-9">
              <span v-if="selectedDocumentType.fechaCreacion">
                {{ formatDate(selectedDocumentType.fechaCreacion) }}
              </span>
              <span class="text-muted" v-else>—</span>
            </dd>

            <dt class="col-sm-3">Entidad Asociada</dt>
            <dd class="col-sm-9">
              <div v-if="selectedDocumentType.entidad">
                <strong>{{ selectedDocumentType.entidad.entidadNombre }}</strong>
                <br />
                <small class="text-muted">
                  ID Entidad: {{ selectedDocumentType.entidad.entidadId }}
                </small>
              </div>
              <span class="text-muted" v-else>—</span>
            </dd>
          </dl>
        </div>
        <template v-slot:modal-footer="{ ok, cancel }">
          <b-button variant="secondary" @click="cancel">Cerrar</b-button>
        </template>
      </b-modal>
    </div>
  </b-overlay>
</template>

<script>
import { getMyDocuments } from '@/febos/servicios/api/docDigital.api';

export default {
  name: 'MyDocuments',
  data() {
    return {
      loading: false,
      documentTypes: [],
      // Paginación
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      totalPages: 1,
      fields: [
        { key: 'tipoId', label: 'ID Tipo', sortable: true },
        { key: 'nombre', label: 'Nombre', sortable: true },
        { key: 'descripcion', label: 'Descripción', sortable: true },
        { key: 'activo', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' }
      ],
      showDetailModal: false,
      selectedDocumentType: null
    };
  },
  computed: {
    emptyText() {
      return this.loading
        ? 'Cargando tipos de documentos...'
        : 'No hay tipos de documentos disponibles';
    },
    modalTitle() {
      return this.selectedDocumentType
        ? `Tipo de Documento: ${this.selectedDocumentType.nombre}`
        : 'Detalles de Tipo de Documento';
    }
  },
  async created() {
    await this.fetchMyDocuments();
  },
  methods: {
    async fetchMyDocuments() {
      this.loading = true;
      try {
        // Llamamos al endpoint para obtener los tipos de documentos
        const response = await getMyDocuments({
          page: this.currentPage,
          perPage: this.perPage
        });

        // La respuesta tiene el formato:
        // {
        //   "respuesta": {
        //     "status": 200,
        //     "message": "OK",
        //     "count": 1,
        //     "timestamp": "24-07-2026 12:05:12",
        //     "result": [
        //       {
        //         "tipoId": 1,
        //         "nombre": "Oficio",
        //         "descripcion": "Documento oficial de comunicación",
        //         "activo": true,
        //         "fechaCreacion": "2024-01-15T10:30:00",
        //         "entidad": {
        //           "entidadId": 100,
        //           "entidadNombre": "Ministerio de..."
        //         }
        //       }
        //     ]
        //   },
        //   "codigo": 10,
        //   ...
        // }

        // La función getMyDocuments devuelve el objeto `respuesta` (el interior)
        // Accedemos a response.result que es el array de tipos de documentos
        if (response && response.result && Array.isArray(response.result)) {
          this.documentTypes = response.result;
          this.totalCount = response.count || response.result.length;
          this.totalPages = Math.ceil(this.totalCount / this.perPage);
        } else {
          this.documentTypes = [];
          this.totalCount = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error('Error al cargar tipos de documentos:', error);
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los tipos de documentos'
        });
        this.documentTypes = [];
        this.totalCount = 0;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    async refreshList() {
      await this.fetchMyDocuments();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchMyDocuments();
    },

    viewDocumentTypeDetails(documentType) {
      this.selectedDocumentType = documentType;
      this.showDetailModal = true;
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      try {
        return new Date(dateString).toLocaleDateString('es-CL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
}

.table td {
  vertical-align: middle;
}

.spinner-border-sm {
  width: 0.75rem;
  height: 0.75rem;
}
</style>
